import { Component, OnInit, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationService, NotificationDto, PaginationInfo } from '../../services/notification.service';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './notification.html',
  styleUrl: './notification.scss'
})
export class Notification implements OnInit, OnDestroy {
  // Observable streams
  unreadCount: number = 0;
  notifications: NotificationDto[] = [];
  
  // UI state
  isDropdownOpen: boolean = false;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  
  // Pagination
  pagination: PaginationInfo = {
    skip: 0,
    take: 20,
    totalCount: 0,
    unreadCount: 0,
    hasMore: false
  };
  
  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private elementRef: ElementRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Subscribe to unread count updates
    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
      });

    // Subscribe to notifications updates
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        this.notifications = notifications;
      });

    // Load initial unread count
    this.loadUnreadCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggle notification dropdown
   */
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    
    if (this.isDropdownOpen && this.notifications.length === 0) {
      // Load notifications when dropdown opens for the first time
      this.loadNotifications();
    }
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  /**
   * Load unread count only
   */
  private loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      error: (err) => {
        console.error('Error loading unread count:', err);
      }
    });
  }

  /**
   * Load notifications (first page)
   */
  loadNotifications(): void {
    this.isLoading = true;
    
    this.notificationService.refreshNotifications().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.notifications = response.data.notifications;
          this.pagination = response.data.pagination;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading notifications:', err);
        this.isLoading = false;
      }
    });
  }

  /**
   * Load more notifications (pagination)
   */
  loadMoreNotifications(): void {
    if (!this.pagination.hasMore || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;
    const nextSkip = this.pagination.skip + this.pagination.take;
    
    this.notificationService.loadMoreNotifications(undefined, undefined, nextSkip, this.pagination.take).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Service handles updating the notifications array
          this.pagination = response.data.pagination;
        }
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error('Error loading more notifications:', err);
        this.isLoadingMore = false;
      }
    });
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notification: NotificationDto, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (notification.isRead) {
      return; // Already read
    }

    this.notificationService.markNotificationAsRead(notification.id).subscribe({
      next: (response) => {
        if (response.success) {
          console.log('Notification marked as read:', notification.id);
        }
      },
      error: (err) => {
        console.error('Error marking notification as read:', err);
      }
    });
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.unreadCount === 0) {
      return; // No unread notifications
    }

    this.notificationService.markAllNotificationsAsRead().subscribe({
      next: (response) => {
        if (response.success) {
          console.log('All notifications marked as read');
        }
      },
      error: (err) => {
        console.error('Error marking all notifications as read:', err);
      }
    });
  }

  /**
   * Handle notification click
   */
  onNotificationClick(notification: NotificationDto): void {
    // Mark as read
    this.markAsRead(notification);

    // Navigate to URL if provided
    if (notification.url) {
      // Close dropdown first for better UX
      this.closeDropdown();
      
      // Map backend URL to I4U route
      const i4uUrl = this.mapBackendUrlToI4U(notification.url);
      
      if (i4uUrl) {
        this.router.navigateByUrl(i4uUrl);
      }
    }
  }

  /**
   * Map backend Blazor URLs to I4U Angular routes
   * Backend stores URLs for Blazor app, this converts them to I4U equivalents
   */
  private mapBackendUrlToI4U(backendUrl: string): string | null {
    if (!backendUrl) return null;
    
    // URL mapping patterns: backend route -> I4U route
    const urlMappings: { pattern: RegExp; transform: (match: RegExpMatchArray) => string }[] = [
      // Agent Commission routes - redirect to commission page
      {
        pattern: /^\/AgentCommissionDetails\/([^\/]+)\/(.+)$/i,
        transform: (match) => `/commission`
      },
      // Application routes
      {
        pattern: /^\/ApplicationAndPolicyUpdate\/(.+)$/i,
        transform: (match) => `/application-detail/${match[1]}`
      },
      {
        pattern: /^\/ApplicationAndPolicyUpdateBof\/(.+)$/i,
        transform: (match) => `/application-detail/${match[1]}`
      },
      {
        pattern: /^\/ApplicationDetail\/(.+)$/i,
        transform: (match) => `/application-detail/${match[1]}`
      },
      // Policy routes
      {
        pattern: /^\/IHPolicyDetail\/(.+)$/i,
        transform: (match) => `/application-policy-list` // Navigate to policy list as I4U doesn't have policy detail
      },
      {
        pattern: /^\/PolicyDetail\/(.+)$/i,
        transform: (match) => `/application-policy-list`
      },
      // Policy change request routes
      {
        pattern: /^\/PolicyChangeRequest\/Detail\/(\d+)$/i,
        transform: (match) => `/policy-change-request/detail/${match[1]}`
      },
      {
        pattern: /^\/PolicyChangeRequestDetail\/(\d+)$/i,
        transform: (match) => `/policy-change-request/detail/${match[1]}`
      },
      {
        pattern: /^\/PolicyChangeRequestBofDetails\/(.+)-PCR-(\d+)$/i,
        transform: (match) => `/policy-change-request/detail/${match[2]}` // Use the ID part
      },
      // Policy cancel routes
      {
        pattern: /^\/PolicyCancel\/(.+)$/i,
        transform: (match) => `/policy-cancel`
      },
      {
        pattern: /^\/PolicyCancelationBofDetails\/(.+)-CAN$/i,
        transform: (match) => `/policy-cancel` // Redirect to policy cancel list
      },
      {
        pattern: /^\/PolicyCancelRequestBofDetails\/(.+)-CAN$/i,
        transform: (match) => `/policy-cancel` // Redirect to policy cancel list
      },
      // Claim routes
      {
        pattern: /^\/Claim\/Accident\/(\d+)$/i,
        transform: (match) => `/claim/accident-update/${match[1]}`
      },
      {
        pattern: /^\/Claim\/Dead\/(\d+)$/i,
        transform: (match) => `/claim/dead-update/${match[1]}`
      },
      {
        pattern: /^\/ClaimDetail\/(.+)$/i,
        transform: (match) => `/claim`
      },
      {
        pattern: /^\/ClaimAccidentUpdateBof\/(.+)$/i,
        transform: (match) => `/claim/accident-update/${match[1]}`
      },
      {
        pattern: /^\/ClaimDeadUpdateBof\/(.+)$/i,
        transform: (match) => `/claim/dead-update/${match[1]}`
      },
      {
        pattern: /^\/I4UClaimAccidentUpdate\/(.+)$/i,
        transform: (match) => `/claim/accident-update/${match[1]}`
      },
      {
        pattern: /^\/I4UClaimDeadUpdate\/(.+)$/i,
        transform: (match) => `/claim/dead-update/${match[1]}`
      },
      // Premium routes
      {
        pattern: /^\/Premium\/History\/(\d+)$/i,
        transform: (match) => `/premium-history-detail/${match[1]}`
      },
      {
        pattern: /^\/PremiumHistory\/(.+)$/i,
        transform: (match) => `/premium-history-list`
      },
      {
        pattern: /^\/I4UPremiumHistoryDetail\/(.+)$/i,
        transform: (match) => `/premium-history-detail/${match[1]}`
      },
      {
        pattern: /^\/PremiumHistoryListBofDetail\/(.+)$/i,
        transform: (match) => `/premium-history-list-detail/${match[1]}`
      },
      {
        pattern: /^\/IHPayPremiumHistory\/(.+)$/i,
        transform: (match) => `/pay-premium-history/${match[1]}`
      },
      {
        pattern: /^\/PayPremiumFromBonusBofDetails\/([^_]+)_(.+)$/i,
        transform: (match) => `/pay-premium-from-bonus-details/${match[1]}/${match[2]}`
      },
      // Commission routes
      {
        pattern: /^\/Commission/i,
        transform: () => `/commission`
      },
      // Product Interest routes
      {
        pattern: /^\/I4UProductInterest/i,
        transform: () => `/product-interest`
      },
      // Home/Dashboard
      {
        pattern: /^\/Dashboard/i,
        transform: () => `/home`
      },
      {
        pattern: /^\/Home/i,
        transform: () => `/home`
      }
    ];

    // Try each mapping pattern
    for (const mapping of urlMappings) {
      const match = backendUrl.match(mapping.pattern);
      if (match) {
        return mapping.transform(match);
      }
    }

    // If URL already starts with a valid I4U route, use it as-is
    const validI4UPrefixes = [
      '/home', '/application-', '/premium-', '/pay-premium', '/bonus-',
      '/commission', '/product-interest', '/hospital-', '/profile',
      '/claim', '/policy-cancel', '/policy-change-request', '/agent-commission',
      '/pay-premium-from-bonus', '/policy-cancelation', '/policy-cancel-request',
      '/premium-history-list', '/I4UProduct'
    ];
    
    if (validI4UPrefixes.some(prefix => backendUrl.toLowerCase().startsWith(prefix.toLowerCase()))) {
      return backendUrl;
    }

    // Log unmapped URLs for debugging (can be removed in production)
    console.warn(`[Notification] No URL mapping found for: ${backendUrl}`);
    
    // Return null if no mapping found - will not navigate
    return null;
  }

  /**
   * Refresh notifications
   */
  refreshNotifications(): void {
    this.loadNotifications();
  }

  /**
   * Get time ago string (helper method)
   */
  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) {
      return this.translate.instant('NOTIFICATION.JUST_NOW') || 'Just now';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} ${this.translate.instant(minutes !== 1 ? 'NOTIFICATION.MINUTES_AGO' : 'NOTIFICATION.MINUTE_AGO') || (minutes !== 1 ? 'minutes ago' : 'minute ago')}`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} ${this.translate.instant(hours !== 1 ? 'NOTIFICATION.HOURS_AGO' : 'NOTIFICATION.HOUR_AGO') || (hours !== 1 ? 'hours ago' : 'hour ago')}`;
    }

    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days} ${this.translate.instant(days !== 1 ? 'NOTIFICATION.DAYS_AGO' : 'NOTIFICATION.DAY_AGO') || (days !== 1 ? 'days ago' : 'day ago')}`;
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return `${weeks} ${this.translate.instant(weeks !== 1 ? 'NOTIFICATION.WEEKS_AGO' : 'NOTIFICATION.WEEK_AGO') || (weeks !== 1 ? 'weeks ago' : 'week ago')}`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} ${this.translate.instant(months !== 1 ? 'NOTIFICATION.MONTHS_AGO' : 'NOTIFICATION.MONTH_AGO') || (months !== 1 ? 'months ago' : 'month ago')}`;
    }

    const years = Math.floor(days / 365);
    return `${years} ${this.translate.instant(years !== 1 ? 'NOTIFICATION.YEARS_AGO' : 'NOTIFICATION.YEAR_AGO') || (years !== 1 ? 'years ago' : 'year ago')}`;
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.isDropdownOpen) {
      this.closeDropdown();
    }
  }
}